/**
 * THE LOBBY ARRIVES WHOLE, OR THE VEIL STAYS UP.
 *
 * The owner, 24 September 2026: "Screens morph around while they load, and
 * loading is chaotic ... use organized loading screens instead of many small
 * loads scattered across a screen."
 *
 * The lobby used to be revealed the moment its modules had STARTED: measured
 * with the snappiness meter on a 110 ms-RTT cold boot, the lobby appeared at
 * 3.9 s and then kept arriving for another 340-430 ms — the quest rows and the
 * pinned Moon Exchange row popped in, the rail grew 74 px and pushed its footer
 * down, the quest count rewrote itself twice, the Moonlight value changed from
 * "—" to "100%" and widened its chip, the avatar swapped the moon for a face,
 * and the four hub paintings were still downloading on screen.
 *
 * The boot veil is already the organized loading screen, so it now holds until
 * every one of those has its final state: the quest rail's first complete
 * render, the Moonlight status, the avatar's face, the hub paintings decoded,
 * the fonts, and the player's language. Each of those settles on failure as
 * well as success, and the whole hold is capped (`LOBBY_ARRIVAL_CAP_MS`), so a
 * read that never answers can only cost its cap, never the lobby itself
 * (docs/asset-loading.md law 2: a gate that can fail to open has deleted the
 * feature behind it).
 */

import { BootVeil } from './boot-veil.js';

/* Long enough for the quest and Moonlight reads on a slow phone link (~2
   round trips at 320 ms plus the decode), short enough that a stalled read
   costs the player a moment rather than the lobby. */
const LOBBY_ARRIVAL_CAP_MS = 3000;

/* The hub tile paintings are CSS backgrounds (hub.css `--hub-art`), so the
   browser cannot discover them until the tiles are laid out — by which time
   the veil was already fading. index.html preloads them at parse time; this
   waits for them to DECODE, because bytes on the wire are not pixels on the
   screen. Must match hub.css (tests/unit/ui/lobbyArrival.test.js).

   The body background (main.css `body::before`) is waited for too: 128 KB
   that a short veil — about 0.6 s on a warm boot — lifted before (boot plan
   B3). Every URL carries its content marker, `?v=` + the first 8 hex of the
   file's sha256, exactly as hub.css, main.css and index.html write it, so it
   is one download kept for a year
   (tests/unit/styles/lobbyArtVersionMarkers.test.js). */
const LOBBY_ART = Object.freeze([
    'assets/images/hub/tile-adventure.webp?v=059544ef',
    'assets/images/hub/tile-friends.webp?v=de22f3ce',
    'assets/images/hub/tile-foundry.webp?v=d3c2b83e',
    'assets/images/hub/tile-collection.webp?v=41acc102',
    'assets/images/background.webp?v=7d889ccc',
]);

/* Settles when the lobby has arrived whole (the veil's own cap does not
   settle it). A cover that is NOT the boot veil — the consent gate, the
   username card — waits on this to hand over to a finished lobby, capped by
   its caller (see whenLobbyArrived). */
let announceArrived = null;
const arrivedPromise = new Promise((resolve) => { announceArrived = resolve; });

/**
 * Resolves once the lobby has arrived whole — or after `capMs`, whichever is
 * first, so a cover waiting on it can never be stranded. Never rejects.
 * @param {{ capMs?: number }} [options]
 */
function whenLobbyArrived({ capMs = 8000 } = {}) {
    return Promise.race([
        arrivedPromise,
        new Promise((resolve) => { setTimeout(resolve, Math.max(0, Number(capMs) || 0)); }),
    ]);
}

/** Resolves once `url` is decoded (or has failed to load) — never rejects. */
function decoded(url) {
    return new Promise((resolve) => {
        try {
            const img = new Image();
            img.decoding = 'async';
            img.src = url;
            if (typeof img.decode === 'function') {
                img.decode().then(resolve, resolve);
            } else {
                img.onload = resolve;
                img.onerror = resolve;
            }
        } catch (_) {
            resolve();
        }
    });
}

/** Resolves when `promise` settles, whatever it settles to. */
function settled(promise) {
    return Promise.resolve(promise).then(() => undefined, () => undefined);
}

/* User Timing marks, one per signal, so a slow lobby can be read off any
   performance trace (DevTools, the snappiness meter): which signal held it. */
function marked(name, promise) {
    return settled(promise).then(() => {
        try { performance.mark(`lobby-arrival:${name}`); } catch (_) { /* no User Timing */ }
    });
}

/**
 * Hold the boot veil until the lobby is whole. Call BEFORE `BootVeil.reveal`.
 *
 * @param {{ waitFor?: Record<string, Promise<unknown>|undefined|null> | Array<Promise<unknown>|undefined|null> }} [options]
 *   the lobby's own readiness signals by name (quest rail, Moonlight, avatar, i18n)
 * @returns {Promise<void>} settles when every signal has settled; the veil
 *   does not wait for it past its own cap
 */
function holdVeilForLobby({ waitFor = [] } = {}) {
    /* The cap is the VEIL's alone (BootVeil.hold caps itself). The covers
       that are not the veil — the username card, a re-consent gate — wait for
       the whole lobby up to the caps they pass to whenLobbyArrived (8 s,
       12 s). This cap used to announce the arrival too: measured 25 September
       2026 on WebKit iPhone landscape, 4G, the username card lifted at 3 s
       and the Quests pill rewrote '0/3' to '2/4' 1.1-2.2 s later. */
    const releaseVeil = BootVeil.hold('lobby-arrival', LOBBY_ARRIVAL_CAP_MS);
    const release = () => {
        releaseVeil();
        if (announceArrived) announceArrived();
    };
    const fonts = typeof document !== 'undefined' && document.fonts && document.fonts.ready;
    const signals = Array.isArray(waitFor)
        ? waitFor.map((promise, index) => [`signal-${index}`, promise])
        : Object.entries(waitFor);
    const everything = [
        marked('art', Promise.all(LOBBY_ART.map(decoded))),
        ...signals.filter(([, promise]) => promise).map(([name, promise]) => marked(name, promise)),
        fonts ? marked('fonts', fonts) : null,
    ].filter(Boolean);
    return Promise.all(everything).then(() => {
        try { performance.mark('lobby-arrival:complete'); } catch (_) { /* no User Timing */ }
        release();
    });
}

/* The hold currently in force, so an older sign-in's cap never lifts a newer one. */
let figuresHolder = null;

/**
 * THE BAR'S FIGURES ARRIVE TOGETHER (17 October 2026).
 *
 * The account bar carries two figures that each wait on a read: the Quests
 * count on the phone's pill and the Moonlight meter. The reads answer at
 * different moments, and on the sign-in path no veil covers the lobby, so
 * they landed one at a time in a bar the player was already reading — the
 * count, then a second later the meter. The bar itself still arrives at once,
 * every pill at its final size with its glyph and label; only its figures
 * stay unpainted (`#user-controls[data-figures="pending"]`, hub.css) until
 * both reads have settled, answer or failure, and then appear in one frame.
 * Capped like the veil, so a read that never answers holds nothing but its
 * own figure (docs/asset-loading.md law 2).
 *
 * @param {HTMLElement|null} strip `#user-controls`
 * @param {{ capMs?: number }} [options]
 * @returns {{ when: (signals: Array<Promise<unknown>|undefined|null>) => void, release: () => void }}
 */
function holdAccountFigures(strip, { capMs = LOBBY_ARRIVAL_CAP_MS } = {}) {
    if (!strip) return { when: () => {}, release: () => {} };
    const token = {};
    figuresHolder = token;
    strip.dataset.figures = 'pending';
    let cap = null;
    const release = () => {
        if (cap !== null) {
            clearTimeout(cap);
            cap = null;
        }
        // A later sign-in's hold is its own: an older one never lifts it.
        if (figuresHolder !== token) return;
        figuresHolder = null;
        delete strip.dataset.figures;
    };
    cap = setTimeout(release, Math.max(0, Number(capMs) || 0));
    return {
        when: (signals = []) => {
            Promise.all(signals.map(settled)).then(release);
        },
        release,
    };
}

export { LOBBY_ARRIVAL_CAP_MS, LOBBY_ART, holdAccountFigures, holdVeilForLobby, whenLobbyArrived };
